'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import AnnouncementBar from 'components/commercetools-ui/organisms/announcement-bar';
import { AnnouncementBarProps, Item } from 'components/commercetools-ui/organisms/announcement-bar/types';
import { useCategoryBar } from 'providers/categoryBar';
import { TasticProps } from 'frontastic/tastics/types';

import { trackViewPromotion } from 'helpers/analytics/ga4Events';
import { isValidForCurrentTime } from 'helpers/utils/util';
import { useFeatures } from 'providers/feature-flag';

const AnnouncementBarTastic = ({ data }: TasticProps<AnnouncementBarProps>) => {
  const { render, items = [] } = data;
  const { announcementBarData, setAnnouncementBarData } = useCategoryBar();
  const { enableScheduleCategoryAnnouncement } = useFeatures();
  const isScheduleCategoryAnnouncementEnabled = enableScheduleCategoryAnnouncement();
  const didFire = useRef(false);
  const filteredItems = useMemo(() => {
    return items?.filter((item: Item) => {
      const hasTitle = Boolean(item.title);
      const hasValidTime = isScheduleCategoryAnnouncementEnabled ? isValidForCurrentTime(item?.validFrom, item?.validTo) : true;
      return hasTitle && hasValidTime;
    });
  }, [items]);

  const shouldRender = Boolean(render && filteredItems.length > 0);

  useEffect(() => {
    if (announcementBarData.render !== shouldRender) {
      setAnnouncementBarData({ render: shouldRender });
    }
  }, [shouldRender, announcementBarData.render, setAnnouncementBarData]);

  const fireViewPromotion = () => {
    const first = filteredItems[0];
    if (!first?.title) return;

    trackViewPromotion({
      creativeName: first.title,
      creativeSlot: 'pos_1',
      promotionId: 'condensedhero_banner_1',
      promotionName: first.title || 'Condensed Hero Banner 1',
    });
  };

  useEffect(() => {
    if (!shouldRender) return;
    if (didFire.current) return;
    if (!filteredItems.length) return;

    fireViewPromotion();
    didFire.current = true;
  }, [shouldRender, filteredItems]);

  return shouldRender ? <AnnouncementBar {...data} items={filteredItems} /> : null;
};

export default AnnouncementBarTastic;
